import React, { useEffect, useState } from 'react'
import {
	Button,
	Drawer,
	Table,
	TablePaginationConfig,
	Tag,
	Tooltip,
} from 'antd'
import moment from 'moment'
import { ClientProfile, Invoice, Plan } from 'types'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import { getInvoicesAction, getSingleInvoicesAction } from 'slices/invoice'
import { EyeOutlined } from '@ant-design/icons'
import SingleInvoiceTable from 'component/Invoices/SingleInvoiceTable'
import addCurrencySymbol from 'utils/addCurrencySymbol'

/**
 * Hardcoded size of the table
 */
const size = 10

interface PropTypes {
	isPaid: boolean | undefined
}

const InvoicesTable: React.FC<PropTypes> = ({ isPaid }) => {
	const [drawer, setDrawer] = useState(false)
	const dispatch = useAppDispatch()
	const { records, currentPage, totalCount, loading, invoice } = useAppSelector(
		state => state.invoice
	)

	/**
	 * Open Drawer for Single invoice view
	 */
	const openDrawer = (id: string) => {
		setDrawer(true)
		dispatch(getSingleInvoicesAction(id))
	}

	/**
	 * Close Drawer for Single invoice view
	 */
	const closeDrawer = () => {
		setDrawer(false)
	}

	/**
	 * Pagination config for the table
	 */
	const pagination = {
		current: currentPage,
		total: totalCount,
		pageSize: size,
	}

	/**
	 * Fetch Invoice on mount and on filter change
	 */

	useEffect(() => {
		const options = {
			page: pagination.current,
			size: pagination.pageSize,
			isPaid,
		}
		dispatch(getInvoicesAction(options))
	}, [dispatch, currentPage, isPaid])

	/**
	 * Handles pagination change by changing the current page in the store.
	 *
	 * @param page - Pagination object from the table
	 */

	const handlePageChange = (page: TablePaginationConfig) => {
		const options = {
			page: page.current || 1,
			size: page.pageSize || size,
			isPaid,
		}
		dispatch(getInvoicesAction(options))
	}

	/**
	 * Columns for the table of Invoice
	 */

	const columns = [
		{
			title: 'Number',
			dataIndex: 'number',
		},
		{
			title: `Amount`,
			dataIndex: 'amount',
			render: (amount: number, record: Invoice) =>
				addCurrencySymbol(
					amount + (record.tdsAmount ?? 0),
					record.currency ?? record.client.currency_selected
				),
		},
		{
			title: `Receiveable`,
			dataIndex: 'amount',
			render: (amount: number, record: Invoice) =>
				addCurrencySymbol(
					amount,
					record.currency ?? record.client.currency_selected
				),
		},
		{
			title: `TDS`,
			dataIndex: 'tdsAmount',
			render: (tdsAmount: number | undefined, record: Invoice) =>
				record.tdsEnabled
					? addCurrencySymbol(
							tdsAmount ?? 0,
							record.currency ?? record.client.currency_selected
					  )
					: '-',
		},
		{
			title: 'Date',
			dataIndex: 'createdAt',
			render: (createdAt: string) => moment(createdAt).format('DD/MM/YYYY'),
		},
		{
			title: 'Due Date',
			dataIndex: 'dueDate',
			render: (dueDate: string | null) =>
				dueDate ? moment(dueDate).format('DD/MM/YYYY') : 'NA',
		},
		{
			title: 'Will Deactivate',
			dataIndex: 'shouldDeactivateAccount',
			render: (deactivation: boolean | undefined) =>
				deactivation ? <Tag color='red'>Yes</Tag> : <Tag color='green'>No</Tag>,
		},
		{
			title: 'Client Name',
			dataIndex: 'client',
			render: (client: ClientProfile) => (client ? client.organizationName : '--'),
		},
		{
			title: 'Client Email',
			dataIndex: 'client',
			render: (client: ClientProfile) => (client ? client.email : '--'),
		},
		{
			title: 'Paid',
			dataIndex: 'isPaid',
			render: (item: boolean) =>
				item ? <Tag color='green'>Yes</Tag> : <Tag color='red'>No</Tag>,
		},
		{
			title: 'Selected Plan',
			dataIndex: 'selectedPlan',
			render: (selectedPlan: Plan) => selectedPlan.billingCycle,
		},
		{
			title: 'Plan Price',
			dataIndex: 'selectedPlan',
			render: (selectedPlan: Plan, record: Invoice) =>
				selectedPlan.billingCycle === 'MONTHLY'
					? addCurrencySymbol(
							selectedPlan.perMonth,
							record.currency ?? record.client.currency_selected
					  )
					: addCurrencySymbol(
							selectedPlan.perYear,
							record.currency ?? record.client.currency_selected
					  ),
		},
		{
			title: 'Action',
			dataIndex: '_id',
			key: 'action',
			render: (_id: string) => (
				<div style={{ display: 'flex' }}>
					<div>
						<Tooltip placement='bottomLeft' title='View'>
							<Button
								type='primary'
								icon={<EyeOutlined />}
								onClick={() => openDrawer(_id)}
							/>
						</Tooltip>
					</div>
				</div>
			),
		},
	]

	return (
		<>
			<Table
				columns={columns}
				rowKey={record => record._id}
				dataSource={records}
				pagination={{ ...pagination, position: ['bottomCenter'] }}
				loading={loading}
				onChange={handlePageChange}
				scroll={{ x: 'fit-content' }}
			/>
			<Drawer
				title={`Invoice Number: ${invoice?.number}`}
				placement='right'
				width='38vw'
				closable
				visible={drawer}
				onClose={closeDrawer}
				destroyOnClose
			>
				<SingleInvoiceTable invoiceData={invoice} loading={loading} />
			</Drawer>
		</>
	)
}

export default InvoicesTable
